/** 회원 타입 */
export type MemberType = 'public' | 'temp' | 'original';

/** 라포 앱 계정 상태 */
export type AppAccountStatus = 'linked' | 'withdrawn' | 'unlinked';

export interface MemberAppAccountFields {
    /** 신규 계정 상태. 있으면 legacy 필드보다 우선한다. */
    appAccountStatus?: AppAccountStatus;
    /** 앱 탈퇴 완료 시각 */
    appWithdrawnAt?: string | null;
    /** 탈퇴 인증 폐기 진행 상태. pending 동안에도 쓰기는 차단한다. */
    appWithdrawalAuthStatus?: 'pending' | 'revoked' | null;
    /** 서버가 내려주는 최종 읽기 전용 판정 */
    isReadOnly?: boolean;
    /** 앱 가입(auth) 여부 (legacy) */
    appLinked?: boolean;
    /** 앱 온보딩 완료 플래그 (legacy) */
    isInfo?: boolean;
}

/**
 * 신규 앱 계정 상태와 legacy 연동 필드를 하나의 상태로 정규화한다.
 * appWithdrawnAt이 내려온 구버전 과도기 응답도 탈퇴로 판정한다.
 */
export const getMemberAppAccountStatus = (
    member: MemberAppAccountFields | null | undefined
): AppAccountStatus => {
    if (!member) return 'unlinked';
    if (
        member.appAccountStatus === 'linked' ||
        member.appAccountStatus === 'withdrawn' ||
        member.appAccountStatus === 'unlinked'
    ) {
        return member.appAccountStatus;
    }
    if (member.appWithdrawnAt) return 'withdrawn';
    return (member.appLinked ?? !!member.isInfo) ? 'linked' : 'unlinked';
};

export const APP_ACCOUNT_STATUS_LABEL: Record<AppAccountStatus, string> = {
    linked: '연동',
    withdrawn: '앱 탈퇴',
    unlinked: '미연동',
};

/**
 * 회원의 앱 연동 여부를 일관되게 판정.
 * 신규 `appAccountStatus`를 우선하고, 구형 응답에서는 `appLinked`/`isInfo`로 폴백한다.
 */
export const isMemberAppLinked = (member: MemberAppAccountFields | null | undefined): boolean =>
    getMemberAppAccountStatus(member) === 'linked';

/** 탈퇴 완료/처리 중 회원 기록의 공통 읽기 전용 판정 */
export const isMemberRecordReadOnly = (
    member: MemberAppAccountFields | null | undefined
): boolean =>
    member?.isReadOnly === true ||
    member?.appWithdrawalAuthStatus === 'pending' ||
    getMemberAppAccountStatus(member) === 'withdrawn';

/** 읽기 전용은 유지하되 센터 목록에서 명시적으로 제거할 수 있는 탈퇴 기록 */
export const isPreservedWithdrawalRecord = (
    member: MemberAppAccountFields | null | undefined
): boolean =>
    member?.appWithdrawalAuthStatus === 'pending' ||
    getMemberAppAccountStatus(member) === 'withdrawn';

/** 센터 회원 상태 */
export type CenterMemberStatus = 'active' | 'inactive' | 'suspended';

/** 등록 방법 */
export type RegistrationMethod = 'excel' | 'kiosk' | 'manual' | 'app_onboarding' | 'pt_register';

/** 센터 회원 (통합 — PT + 시설 정보 포함) */
export interface ICenterMember {
    _id: string;
    centerId: string;
    memberId: string;
    status: CenterMemberStatus;
    joinedAt: string;
    registrationMethod: RegistrationMethod;
    registeredBy?: {
        userId: string;
        role: string;
        name: string;
    };
    memo?: string;
    isDeleted: boolean;
    deletedAt?: string;
    /** member 정보 (flat) */
    name: string;
    phoneNumber: string;
    gender?: 'male' | 'female';
    birthday?: string;
    /** 집 주소. 구서버는 내려주지 않는다('' 가능). */
    address?: string;
    /** 키(cm). 구서버는 내려주지 않는다. */
    height?: number | null;
    /** 시작 체중(kg). 구서버는 내려주지 않는다. */
    startWeight?: number | null;
    memberType: MemberType;
    profileImageUrl?: string;
    /** PT 정보 (rapport 기반) */
    hasPT: boolean;
    ptStatus?: 'active' | 'expired' | 'empty' | 'none';
    coachName: string;
    coachId: string;
    /** 그룹수강권 정보 */
    hasGroupTicket?: boolean;
    groupTicketCount?: number;
    groupTickets?: Array<{
        memberTicketId: string;
        ticketId: string;
        name: string;
        status: string;
        totalCount: number;
        usedCount: number;
        remainingCount: number;
        startDate?: string;
        expiryDate?: string;
    }>;
    /** 시설 이용권 정보 */
    hasSubscription: boolean;
    subscriptionCount: number;
    /** 시설 이용권 상세 (응답에 포함 시 hover 팝업에 노출) */
    subscriptions?: Array<{
        subscriptionId: string;
        productName: string;
        category?: string;
        status: string;
        startDate?: string;
        endDate?: string;
        lockerNumber?: string | null;
    }>;
    /** 앱 온보딩 완료 플래그 (legacy) — 표시에는 appLinked 사용 권장 */
    isInfo?: boolean;
    /** 앱 가입(auth) 여부 — 표시·필터의 정확한 기준 */
    appLinked?: boolean;
    /** 앱 계정 상태 — 표시·필터의 우선 기준 */
    appAccountStatus?: AppAccountStatus;
    /** 앱 탈퇴 완료 시각 */
    appWithdrawnAt?: string;
    /** 앱 가입 후 정보 입력 대기 여부. 구서버는 내려주지 않는다. */
    onboardingPending?: boolean;
    /** 정보 입력 없이 앱 가입 후 30일 경과 여부 */
    onboardingStale?: boolean;
    /** 앱 정보 입력 대기 시작 시각 */
    onboardingPendingSince?: string | null;
    appWithdrawalAuthStatus?: 'pending' | 'revoked' | null;
    isReadOnly?: boolean;
    /** PT 수강권 개수 */
    ptCount?: number;
    /** 최근 수업일 */
    lastLessonAt?: string;
    /** 수강권 상세 (V1 API — 모든 강사 통합) */
    memberships?: Array<{
        membershipId: string;
        name: string;
        coachId: string;
        coachName: string;
        status: string;
        currentSession: number;
        totalSession: number;
        remainSession: number;
        activationDate?: string;
        expirationDate?: string;
    }>;
}

/** 회원 단건 등록 바디 */
export interface CreateCenterMemberBody {
    name: string;
    phoneNumber: string;
    memberType?: MemberType;
    gender?: 'male' | 'female';
    birthday?: string;
    memo?: string;
    isSendAlimtalkToMember?: boolean;
}

/** 회원 수정 바디 */
export interface UpdateCenterMemberBody {
    name?: string;
    phoneNumber?: string;
    gender?: 'male' | 'female';
    birthday?: string;
    memo?: string;
    /**
     * 집 주소('' = 지우기). 구서버 Joi 는 모르는 키를 400 으로 거절하므로
     * 값을 바꿨을 때만 싣는다.
     */
    address?: string;
}

/** 회원 타입 전환 바디 */
export interface ConvertMemberTypeBody {
    targetType: MemberType;
    reason?: string;
}

/** 엑셀 일괄 등록 결과 */
export interface BulkUploadResult {
    success: number;
    skipped: number;
    errors: Array<{
        row: number;
        name: string;
        phoneNumber: string;
        reason: string;
    }>;
}

/** 회원 목록 조회 파라미터 */
export type FacilityStatus = 'active' | 'expiring' | 'expired';

export interface PublicMemberListParams {
    centerId: string;
    memberType?: MemberType | 'all';
    status?: CenterMemberStatus | 'all';
    search?: string;
    hasPT?: 'true' | 'false';
    memberScope?: 'all' | 'lesson' | 'public' | 'pt' | 'group';
    coach?: string;
    remainingPeriod?: string;
    remainingSessions?: string;
    displayStatus?: 'active' | 'pending' | 'expired' | 'all';
    facilityStatus?: FacilityStatus | 'all';
    appAccountStatus?: AppAccountStatus | 'all';
    /** @deprecated 신규 구현은 appAccountStatus 사용 */
    appLinked?: 'linked' | 'unlinked' | 'all';
    page?: number;
    limit?: number;
    timeout?: number;
}

export interface PublicMemberTypeCounts {
    public: number;
    original: number;
    temp: number;
    nonPublic: number;
}

export interface PublicMemberCategoryCounts {
    public: number;
    lesson: number;
}

/** 회원 목록 조회 응답 */
export interface PublicMemberListResponse {
    results: ICenterMember[];
    page: number;
    limit: number;
    totalPages: number;
    totalResults: number;
    memberTypeCounts?: PublicMemberTypeCounts;
    memberCategoryCounts?: PublicMemberCategoryCounts;
}

/** PT 관계 */
export interface PTRelation {
    coachId: string;
    coachName?: string;
    status: string;
    alias: string;
    relationStatus?: 'temp' | 'onProgress' | 'active';
    onboardingPending?: boolean;
    onboardingStale?: boolean;
}

/** PT 멤버십 (세션 정보) */
export interface PTMembership {
    name: string;
    totalSession: number;
    currentSession: number;
    remainSession: number;
}

/** PT 정보 */
export interface PTInfo {
    hasActivePT: boolean;
    relations: PTRelation[];
    memberships: PTMembership[];
}

/** 회원 상세 이용권 (API 응답 포맷) */
export interface MemberSubscriptionInfo {
    productName: string;
    category: string;
    startDate: string;
    endDate: string;
    status: string;
    lockerNumber?: string;
}

/** 회원 상세 최근 출입 */
export interface MemberRecentCheckin {
    checkinAt: string;
    method: string;
}

/** 아카이브 회원 */
export interface IMemberArchive {
    _id: string;
    centerId: string;
    memberId: string;
    coachId: string;
    coachName: string;
    archivedAt: string;
    reason: string;
    memberName: string;
    phoneNumber: string;
    isReturned: boolean;
    returnedAt?: string;
}

/** 아카이브 회원 목록 응답 */
export interface MemberArchiveListResponse {
    results: IMemberArchive[];
    page: number;
    limit: number;
    totalPages: number;
    totalResults: number;
}

/** 회원 상세 조회 응답 */
export interface PublicMemberDetailResponse {
    centerMember: ICenterMember;
    member: {
        name: string;
        phoneNumber: string;
        memberType: MemberType;
        gender?: string;
        birthday?: string;
        address?: string;
        height?: number | null;
        startWeight?: number | null;
        appAccountStatus?: AppAccountStatus;
        appWithdrawnAt?: string;
        appLinked?: boolean;
        isInfo?: boolean;
    };
    subscriptions: MemberSubscriptionInfo[];
    ptInfo: PTInfo;
    recentCheckins: MemberRecentCheckin[];
    memberTypeHistory: Array<{
        from: string;
        to: string;
        changedAt: string;
        reason: string;
    }>;
}
